import { get } from 'lodash';
import { useMemo } from 'react';
import { FieldPath, UseFormReturn } from 'react-hook-form';
import { FieldValues, FieldsElementType, IUIField, FormProps, ISchemaForm, IUIKey } from './utils';

interface Props<
  T extends FieldValues = FieldValues,
  K extends IUIKey = string,
  F extends FormProps = FormProps,
  R extends FieldValues = T,
> {
  methods: UseFormReturn<R>;
  formProps: F;
  schema?: ISchemaForm<T, K, F, R>;
  fieldName?: FieldPath<R>;
  showSubmitButton?: boolean;
  [key: string]: any;
}

const useMappedFieldForm = <
  T extends FieldValues = FieldValues,
  K extends IUIKey = string,
  F extends FormProps = FormProps,
  R extends FieldValues = T,
>(
  props: Props<T, K, F, R>
) => {
  const { formProps, methods, fieldName, schema } = props;

  const groupFields = useMemo((): FieldsElementType[] => {
    if (!schema) {
      return [];
    }
    const valuesField = (
      fieldName ? methods?.getValues(fieldName) || {} : methods?.getValues() || {}
    ) as T;
    const fields =
      typeof schema?.fields === 'function'
        ? schema?.fields({ valuesField, methods, fieldName, formProps })
        : schema?.fields;
    const ui =
      typeof schema?.ui === 'function'
        ? schema?.ui({ valuesField, methods, formProps })
        : schema?.ui;

    const groupFields: FieldsElementType[] = ui
      ? ui
          .filter(Boolean)
          .reduce((value: FieldsElementType[], current: IUIField<T>) => {
            const flatValue: string[] = value
              .filter((v) => !v.hidden)
              .reduce((val: string[], cur: FieldsElementType) => {
                return [
                  ...val,
                  ...(cur.fields ?? []).map((v: any) => v.key_element).filter(Boolean),
                ];
              }, []);
            const tmp = (current?.fields as string[]) ?? [];
            return [
              ...value,
              {
                ...current,
                fields: (Array.isArray(tmp) ? tmp : [])
                  .filter((val) => val && !flatValue.includes(val as string))
                  .map((val: string) => {
                    const field = get(fields, val) as any;
                    return (
                      field && {
                        ...formProps.globalProps,
                        ...field,
                        key_element: val,
                      }
                    );
                  })
                  .filter(Boolean),
              },
            ];
          }, [])
          .filter(Boolean)
      : [
          {
            id: 'default',
            fields: Object.entries(fields).map(([key, value]) => {
              return {
                key_element: key,
                ...formProps.globalProps,
                ...value,
              };
            }),
          },
        ];

    return groupFields;
  }, [fieldName, formProps, methods, schema]);

  return { groupFields };
};
export default useMappedFieldForm;
